import { Alert, CloseButton, Col,Row,Breadcrumb,Button, FloatingLabel, Form as BootstrapForm, Modal, ProgressBar } from "react-bootstrap";
import LoggedInLayout from "../../layouts/loggedin-layout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faMagnifyingGlass, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef, useState } from "react";
import ButtonOverride from "../../components/buttonoverride";
import { useForm } from "react-hook-form";
import { useClientSearch } from "../../new/clientsearch.query";
import UserManSkeleton from "../../components/usermanagementskeleton";
import ClientSearchListItem from "../../data/clientsearchlistitem";
import InsetAlert from "../../components/insetalert";
import useClientSearchStore from "../../stores/client-search-store";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

const paramConfig ={
    primary: {
        applicationid: {weight: 100,type: 'number',label: "Application ID"},
        medicaidid: {weight: 100,type: 'number',label: "Medicaid ID"}
    },
    secondaryGroupings: [
        {
            firstname: {weight: 20, type: 'text',label: "First Name"},
            lastname: {weight: 20, type: 'text',label: "Last Name"},
            groupWeight: 100,
            groupTitle: "Identifying Information"
        }
    ]
}

const requiredWeight = 100;

const getInitialParams = () => {
    const initialParams = {};

    Object.keys(paramConfig.primary).forEach(key => {
        initialParams[key] = '';
    });

    paramConfig.secondaryGroupings.forEach(group => {
        Object.keys(group).filter(key => key !== 'groupWeight' && key !== 'groupTitle').forEach(key => {
            initialParams[key] = '';
        });
    });

    return initialParams;
}

const normalizeSearchParams = (params) => {
    return Object.entries(params).reduce((acc,[key,value]) => {
        acc[key] = typeof value === "string" ? value.trim() : value;
        return acc;
    },{});
}

const ClientSearch = () => {
    const [searchParams,setSearchParams] = useState(getInitialParams);
    const [showSearchModal,setShowSearchModal] = useState(false);
    const [activeInput,setActiveInput] = useState('');
    const [hasSearched,setHasSearched] = useState(false);
    const [searchFeedback,setSearchFeedback] = useState(null);
    const [results,setResults] = useState([]);
    const searchMutation = useClientSearch();
    const navigate = useNavigate();
    const location = useLocation();
    const navigationType = useNavigationType();
    const savedSnapshot = useClientSearchStore((state) => state.snapshot);
    const setSavedSnapshot = useClientSearchStore((state) => state.setSnapshot);
    const clearSavedSnapshot = useClientSearchStore((state) => state.clearSnapshot);
    const didHydrateRef = useRef(false);
    const scrollRestoreRef = useRef(null);
    const {
        register,
        handleSubmit,
        watch,
        reset,
        setFocus
    } = useForm({
        defaultValues: getInitialParams()
    });


    useEffect(() => {
        document.title = "MFP - Client Search";
    },[]);

    useEffect(() => {
        if(didHydrateRef.current){
            return;
        }

        const initialParams = getInitialParams();
        const shouldRestore =
            Boolean(savedSnapshot?.pendingRestore) &&
            (
                navigationType === "POP" ||
                location.state?.restoreClientSearch === true
            );

        if(shouldRestore){
            const restoredParams = savedSnapshot?.searchParams
                ? { ...initialParams, ...savedSnapshot.searchParams }
                : initialParams;

            setSearchParams(restoredParams);
            reset(restoredParams);
            setResults(savedSnapshot?.results ?? []);
            setHasSearched(Boolean(savedSnapshot?.hasSearched));
            setSearchFeedback(savedSnapshot?.searchFeedback ?? null);
            scrollRestoreRef.current = savedSnapshot?.scrollY ?? 0;
            setSavedSnapshot({pendingRestore: false});
        }
        else{
            setSearchParams(initialParams);
            reset(initialParams);
            setResults([]);
            setHasSearched(false);
            setSearchFeedback(null);
            scrollRestoreRef.current = null;
            clearSavedSnapshot();
        }

        didHydrateRef.current = true;
    },[savedSnapshot, reset, navigationType, location.state, setSavedSnapshot, clearSavedSnapshot]);

    useEffect(() => {
        if(showSearchModal && activeInput){
            setFocus(activeInput);
        }
    },[showSearchModal, activeInput, setFocus]);

    useEffect(() => {
        if(!didHydrateRef.current){
            return;
        }

        if(!hasSearched && !Object.values(searchParams).some(Boolean) && results.length === 0 && searchFeedback === null){
            clearSavedSnapshot();
            return;
        }

        setSavedSnapshot({
            searchParams,
            results,
            hasSearched,
            searchFeedback
        });
    },[searchParams, results, hasSearched, searchFeedback, setSavedSnapshot, clearSavedSnapshot]);

    useEffect(() => {
        if(scrollRestoreRef.current === null){
            return;
        }

        const nextScrollY = scrollRestoreRef.current;
        scrollRestoreRef.current = null;

        const timer = window.setTimeout(() => {
            window.scrollTo({
                top: nextScrollY,
                left: 0,
                behavior: "instant"
            });
        },0);

        return () => window.clearTimeout(timer);
    },[results.length, hasSearched]);

    const extractSuppliedParams = (params) => {
        const filteredParams = Object.entries(params).reduce((acc,[key,value]) => {
            if(value){
                acc[key] = value;
            }

            return acc;
        },{});

        return filteredParams;
    };

    const runSearch = async (paramsToSearch) => {
        setHasSearched(true);
        setSearchFeedback(null);

        const normalizedParams = normalizeSearchParams(paramsToSearch);
        const dataToSend = extractSuppliedParams(normalizedParams);
        const nextResults = await searchMutation.mutateAsync(dataToSend);
        const safeResults = Array.isArray(nextResults) ? nextResults : [];
        setResults(safeResults);

        setSearchFeedback(
            safeResults.length > 0
            ?
                {
                    variant: "success",
                    body: `Found ${safeResults.length} matching client${safeResults.length === 1 ? '' : 's'}.`
                }
            :
                {
                    variant: "warning",
                    body: "Search completed with no matching clients."
                }
        );

        return { normalizedParams, safeResults };
    };

    const renderInputFields = () => {
        const renderField = (key, config) => {
            if(config.type === 'date'){
                return(
                    <BootstrapForm.Group className="mb-3" key={key}>
                        <BootstrapForm.Label>{config.label}</BootstrapForm.Label>
                        <BootstrapForm.Control
                            type={config.type}
                            max={new Date().toISOString().split('T')[0]}
                            autoComplete="off"
                            {...register(key)}
                        />
                    </BootstrapForm.Group>
                );
            }

            return(
                <FloatingLabel 
                    key={key}
                    label={config.label}
                    className="mb-3"
                >
                    <BootstrapForm.Control
                        type={config.type}
                        placeholder={config.label}
                        autoComplete="off"
                        inputMode={config.type === "number" ? "numeric" : undefined}
                        {...register(key)}
                    />
                </FloatingLabel>
            );
        };

        return(
            <div>
                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                        <small className="text-muted">Use Application ID, Medicaid ID, or both first and last name.</small>
                        <strong>{currentWeight} / {requiredWeight}</strong>
                    </div>
                    <ProgressBar variant={currentWeight >= requiredWeight ? "success" : "primary"} now={Math.min(currentWeight, requiredWeight)} />
                </div>
                <div className="mb-3">
                    {renderSearchRequirements(currentFormValues)}
                </div>
                <div className="mb-3">
                    <div className="mb-2">
                        <h6 className="mb-1">Primary Identifiers</h6>
                        <small className="text-muted">Best for exact matches.</small>
                    </div>
                    {Object.entries(paramConfig.primary).map(([key, config]) => renderField(key, config))}
                </div>
                {
                    paramConfig.secondaryGroupings.map((group,index) => (
                        <div className="mb-3" key={index}>
                            <div className="mb-2">
                                <h6 className="mb-1">{group.groupTitle}</h6>
                                <small className="text-muted">Use the complete set in this section when an ID is not available.</small>
                            </div>
                            {
                                Object.keys(group).filter(key => key !== 'groupWeight' && key !== 'groupTitle').map(key => renderField(key, group[key]))
                            }
                        </div>
                    ))
                }
            </div>
        )
    }

    const calculateSearchParamWeights = (params) => {
        let totalWeight = 0;

        for (const key in paramConfig.primary){
            if(params[key]){
                totalWeight += paramConfig.primary[key].weight;
            }
        }

        paramConfig.secondaryGroupings.forEach(group => {
            const groupKeys = Object.keys(group).filter(key => key !== 'groupWeight' && key !== 'groupTitle');
            const groupWeight = groupKeys.every(key => params[key]) ? group.groupWeight : 0;
            const individualWeights = groupKeys.reduce((total,key) => total + (params[key] ? group[key].weight : 0),0);

            totalWeight += Math.max(groupWeight,individualWeights);
        });

        return totalWeight;
    }
    

    const currentFormValues = watch();
    const currentWeight = calculateSearchParamWeights(currentFormValues);
    const hasActiveSearch = Object.values(searchParams).some(value => Boolean(value));
    const processing = searchMutation.isPending;
    const error = searchMutation.error ? { error: searchMutation.error.message ?? "Unable to search for clients." } : null;

    const renderSearchRequirements = (params) => {
        return (
            <div className="rounded-4 border bg-light p-3">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                    <div>
                        <h6 className="mb-1">Search Requirements</h6>
                        <small className="text-muted">Use one exact identifier or complete the full name pair.</small>
                    </div>
                    <strong>{calculateSearchParamWeights(params)} / {requiredWeight}</strong>
                </div>
                {
                    Object.entries(paramConfig.primary).map(([name,param]) => (
                        <div className="d-flex justify-content-between align-items-center py-1" key={name}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    {
                                        params[name] 
                                        ?
                                            <span className="me-2"><FontAwesomeIcon icon={faCheck} style={{color: '#28a745'}}/></span>
                                        :
                                            <span className="me-2"><FontAwesomeIcon icon={faXmark} style={{color: '#dc3545'}} /></span>
                                    }
                                    <span>{param.label}</span>
                                </div>
                            </div>
                            <small className="fw-bold">{param.weight}</small>
                        </div>
                    ))
                }
                {
                    paramConfig.secondaryGroupings.map((group,index) => (
                        <div className="d-flex justify-content-between align-items-center py-1" key={index}>
                            <div>
                                {
                                    Object.keys(group).filter(key => key !== 'groupTitle' && key !== 'groupWeight').every(paramName => params[paramName])
                                    ?
                                        <span className="me-2"><FontAwesomeIcon icon={faCheck} style={{color: '#28a745'}}/></span>
                                    :
                                        <span className="me-2"><FontAwesomeIcon icon={faXmark} style={{color: '#dc3545'}} /></span>
                                }
                                <span>{Object.keys(group).filter(key => key !== 'groupTitle' && key !== 'groupWeight').map(paramName => group[paramName].label).join(' + ')}</span>
                            </div>
                            <small className="fw-bold">{group.groupWeight}</small>
                        </div>
                    ))
                }
            </div>
        )
    }

    const openSearchModal = (paramsToEdit, focusKey = '') => {
        reset({...paramsToEdit});
        setActiveInput(focusKey);
        setShowSearchModal(true);
    };

    const handleClearSearch = () => {
        const initialParams = getInitialParams();

        setSearchParams(initialParams);
        reset(initialParams);
        setResults([]);
        setHasSearched(false);
        setSearchFeedback(null);
        searchMutation.reset();
        clearSavedSnapshot();
        setShowSearchModal(false);
        setActiveInput('');
    };

    const handleSearchUpdate = async (values) => {
        const normalizedParams = normalizeSearchParams(values);
        const nextWeight = calculateSearchParamWeights(normalizedParams);

        if(nextWeight < requiredWeight){
            return;
        }

        setSearchParams(normalizedParams);
        setShowSearchModal(false);
        setActiveInput('');
        await runSearch(normalizedParams);
    }


    const findLabelForParams = (paramName,config) => {
        if(config.primary[paramName]){
            return config.primary[paramName].label;
        }

        for(const group of config.secondaryGroupings){
            if(group[paramName]){
                return group[paramName].label;
            }
        }

        return paramName;
    }

    const handleOpenClientRecord = (clientInfo) => {
        setSavedSnapshot({
            searchParams,
            results,
            hasSearched,
            searchFeedback,
            scrollY: window.scrollY,
            pendingRestore: true
        });

        navigate(`/Client/Client/${clientInfo.medicaididenc}`);
    };

    return(
        <>
            <LoggedInLayout>
            <div style={{padding: '25px'}}>
                <Row className="mb-4">
                    <Col xs={12}>
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                            <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2">
                                <h3 className="me-3">Client Search</h3>
                                <Breadcrumb>
                                    <Breadcrumb.Item className="primarybreadcrumb">Client</Breadcrumb.Item>
                                    <Breadcrumb.Item className="secondarybreadcrumb" active>Client Search</Breadcrumb.Item>
                                </Breadcrumb>
                            </div>
                            <div>
                                <Button variant="success" onClick={() => openSearchModal(hasActiveSearch ? searchParams : getInitialParams(),'applicationid')} disabled={processing}>
                                    <span className="me-2"><FontAwesomeIcon icon={faMagnifyingGlass} /></span>
                                    {hasActiveSearch ? "Edit Search" : "Start Search"}
                                </Button>
                            </div>
                        </div>
                        <div className="mt-3 p-3 rounded-4 border bg-light">
                            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                                <div>
                                    <div className="fw-semibold">Current Search</div>
                                    <small className="text-muted">Use Application ID, Medicaid ID, or both first and last name. Select a tag below to edit that field.</small>
                                </div>
                                {
                                    hasActiveSearch
                                    ?
                                        <Button variant="link" className="p-0 text-decoration-none" onClick={handleClearSearch} disabled={processing}>Clear Search</Button>
                                    :
                                        null
                                }
                            </div>
                            {
                                hasActiveSearch
                                ?
                                    <div className="search-tags mt-3">
                                        {
                                            Object.entries(searchParams).map(([name, value]) => {
                                                if(value){
                                                    const label = findLabelForParams(name,paramConfig);
                                                    return(
                                                        <ButtonOverride key={name} type="button" className="searchtag" onClick={() => openSearchModal(searchParams,name)}>
                                                            <span className="d-flex align-items-center">
                                                                <span className="slabel me-1">{label}</span> : <span className="svalue ms-1">{value}</span>
                                                            </span>
                                                        </ButtonOverride>
                                                    )
                                                }

                                                return null;
                                            })
                                        }
                                    </div>
                                :
                                    <div className="text-muted mt-3">No search is active yet.</div>
                            }
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                            <div>
                                <h4 className="mb-1">Results</h4>
                                <small className="text-muted">
                                    {
                                        !hasSearched
                                        ? "Run a search to see matching clients."
                                        : error
                                            ? "There was a problem running the current search."
                                        : results.length === 0
                                            ? "No clients matched the current search."
                                            : `${results.length} client${results.length === 1 ? '' : 's'} found.`
                                    }
                                </small>
                            </div>
                        </div>
                        {
                            searchFeedback
                            ?
                                <Alert variant={searchFeedback.variant} className="py-2 px-3">
                                    <div className="d-flex align-items-center justify-content-between gap-3">
                                        <span>{searchFeedback.body}</span>
                                        <CloseButton onClick={() => setSearchFeedback(null)} aria-label="Dismiss search feedback" />
                                    </div>
                                </Alert>
                            :
                                null
                        }
                        {
                            processing
                            ?
                                <UserManSkeleton />
                            :
                                error && hasSearched
                                ?
                                    <div className="d-flex justify-content-center mb-2">
                                        <InsetAlert error={error} dismissible={false} />
                                    </div>
                                :
                                    !hasSearched
                                    ?
                                        <div className="p-4 rounded-4 border text-center bg-light">
                                            <h5 className="mb-2">Start with a client search</h5>
                                            <p className="text-muted mb-0">Use the search button above to look up a client by ID or name.</p>
                                        </div>
                                    :
                                        results.length === 0
                                        ?
                                            <div className="p-4 rounded-4 border text-center bg-light">
                                                <h5 className="mb-2">No results found</h5>
                                                <p className="text-muted mb-3">Try updating the search and running it again.</p>
                                                <Button variant="outline-success" onClick={() => openSearchModal(searchParams,'applicationid')}>Edit Search</Button>
                                            </div>
                                        :
                                            results.map((item,index) => (
                                                <ClientSearchListItem key={index} clientInfo={item} clickActionCallback={handleOpenClientRecord}/>
                                            ))
                        }
                    </Col>
                </Row>
            </div>
        </LoggedInLayout>
        <Modal show={showSearchModal} onHide={() => {setShowSearchModal(false); setActiveInput('');}} size="lg" centered>
            <BootstrapForm onSubmit={handleSubmit(handleSearchUpdate)}>
            <Modal.Header closeButton>
                <Modal.Title>Search Clients</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {
                    renderInputFields()
                }
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between flex-wrap gap-2">
                <Button
                    variant="link"
                    type="button"
                    className="p-0 text-decoration-none"
                    onClick={() => {
                        const initialParams = getInitialParams();
                        reset(initialParams);
                        setActiveInput('applicationid');
                        setFocus('applicationid');
                    }}
                >
                    Clear Fields
                </Button>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" type="button" onClick={() => {setShowSearchModal(false); setActiveInput('');}}>Cancel</Button>
                    <Button variant="success" type="submit" disabled={currentWeight < requiredWeight || processing}>{processing ? "Searching..." : "Search Clients"}</Button>
                </div>
            </Modal.Footer>
            </BootstrapForm>
        </Modal>
        </>
    )
}

export default ClientSearch;
